import React, { useState, useEffect } from "react";
import {
  SortableContainer as sortableContainer,
  SortableElement as sortableElement,
} from "react-sortable-hoc";
import arrayMove from "array-move";
import { makeStyles } from "@mui/styles";
import ChipCobertura from "~shared/chipCobertura/ChipCobertura";
import { Typography } from "@mui/material";
import { EsquemaRow, IdCobertura, SortEnd } from "../types";

const useStyles = makeStyles((theme) => ({
  chip: {
    margin: theme.spacing(0.5, 0.25),
  },
  sinEsquemas: {
    display: "flex",
    color: theme.colours.brownGrey2,
    lineHeight: "13px",
    fontSize: "1em",
    textAlign: "center",
    padding: 20,
    textTransform: "uppercase",
    justifyContent: "center",
    alignItems: "center",
  },
  sortableContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    margin: 0,
    padding: 0,
  },
}));

interface ListSortableProps {
  value: EsquemaRow;
  onChange?: (items: IdCobertura[]) => void;
  onClickCobertura?: (idCompania: string, cobertura: IdCobertura) => void;
}

const ListSortable: React.FC<ListSortableProps> = ({
  value,
  onChange,
  onClickCobertura,
}) => {
  const [items, setItems] = useState<IdCobertura[]>(value.idsCoberturas);

  useEffect(() => {
    setItems(value.idsCoberturas);
  }, [value]);

  const onSortEnd = ({ oldIndex, newIndex }: SortEnd) => {
    if (oldIndex !== newIndex) {
      const newItems: IdCobertura[] = arrayMove(items, oldIndex, newIndex);
      setItems(newItems);
      if (onChange) onChange(newItems);
    }
  };

  const handleDelete = (cobertura: IdCobertura) => {
    const index = items.indexOf(cobertura);
    items.splice(index, 1);
    setItems(items);
    if (onChange) onChange(items);
  };

  const handleClick = (cobertura: IdCobertura) => {
    if (onClickCobertura) onClickCobertura(value.idCompania, cobertura);
  };

  const classes = useStyles();

  return (
    <SortableContainer pressDelay={200} axis="x" onSortEnd={onSortEnd}>
      {items.length === 0 && (
        <Typography className={classes.sinEsquemas}>
          Sin coberturas seleccionadas
        </Typography>
      )}
      {items.map((cobertura, index) => (
        <SortableItem
          key={`item-${cobertura.idCobertura}`}
          index={index}
          value={cobertura}
          onClick={handleClick}
          onDelete={handleDelete}
        />
      ))}
    </SortableContainer>
  );
};

export default ListSortable;

interface SortableItemInnerProps {
  value: IdCobertura;
  onClick: (cobertura: IdCobertura) => void;
  onDelete: (cobertura: IdCobertura) => void;
}

const SortableItem = sortableElement(
  ({ value, onClick, onDelete }: SortableItemInnerProps) => {
    return (
      <div style={{ padding: "5px 5px 0px 0px" }}>
        <ChipCobertura
          label={value.idCobertura}
          onClick={() => {
            if (onClick) onClick(value);
          }}
          onDelete={() => {
            if (onDelete) onDelete(value);
          }}
          tipoCobertura={value.tipoCobertura}
          clickable={true}
        />
      </div>
    );
  },
);

interface SortableContainerInnerProps {
  children: React.ReactNode;
}

const SortableContainer = sortableContainer(
  ({ children }: SortableContainerInnerProps) => {
    const classes = useStyles();
    return <ul className={classes.sortableContainer}>{children}</ul>;
  },
);
